import { useMemo } from 'react';
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from '@azure/msal-react';
import { Outlet } from 'react-router-dom';
import { useAuth } from './useAuth';

function LoginScreen() {
  const { login } = useAuth();

  // Generate random particles once
  const particles = useMemo(
    () =>
      Array.from({ length: 20 }, () => ({
        width: Math.random() * 4 + 2,
        left: Math.random() * 100,
        top: Math.random() * 100,
        duration: Math.random() * 10 + 15,
        delay: Math.random() * 5,
      })),
    [],
  );

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated gradient background */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(135deg, #0B1121 0%, #0f2847 25%, #0d6eaa 50%, #0f2847 75%, #0B1121 100%)',
          backgroundSize: '400% 400%',
          animation: 'gradientShift 15s ease infinite',
        }}
      />

      {/* Subtle grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {particles.map((p, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-primary/20"
            style={{
              width: `${p.width}px`,
              height: `${p.width}px`,
              left: `${p.left}%`,
              top: `${p.top}%`,
              animation: `particle-float ${p.duration}s linear infinite`,
              animationDelay: `${p.delay}s`,
            }}
          />
        ))}
      </div>

      {/* Radial glow behind card */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-30"
        style={{
          background: 'radial-gradient(circle, rgba(3,169,244,0.15) 0%, transparent 70%)',
        }}
      />

      {/* Main content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4">
        {/* Login card */}
        <div
          className="w-full max-w-md"
          style={{ animation: 'fadeInUp 0.8s ease-out' }}
        >
          {/* Glass card */}
          <div
            className="relative backdrop-blur-xl rounded-2xl p-8 md:p-10 border border-white/10"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
              boxShadow: `
                0 25px 50px -12px rgba(0,0,0,0.5),
                0 0 0 1px rgba(255,255,255,0.05) inset,
                0 -20px 40px -20px rgba(3,169,244,0.1) inset
              `,
            }}
          >
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <div
                className="relative"
                style={{ animation: 'logoFloat 3s ease-in-out infinite' }}
              >
                <img
                  src="/logo.png"
                  alt="Frederick W Smith International Airport"
                  className="h-20 w-auto drop-shadow-2xl rounded-lg"
                  style={{
                    filter: 'drop-shadow(0 0 20px rgba(3,169,244,0.3))',
                  }}
                />
              </div>
            </div>

            {/* Title */}
            <div className="text-center mb-8">
              <h1
                className="text-3xl md:text-4xl font-bold tracking-tight mb-2 font-heading"
                style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #7DD3FC 50%, #ffffff 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                AI Hub
              </h1>
              <p className="text-sky-200/80 text-sm font-medium tracking-wider uppercase font-body">
                Frederick W Smith International Airport
              </p>
            </div>

            {/* Divider with airplane */}
            <div className="flex items-center gap-4 mb-8">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-sky-400/30 to-transparent" />
              <svg className="w-4 h-4 text-sky-400/50" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
              </svg>
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-sky-400/30 to-transparent" />
            </div>

            {/* Sign in button */}
            <button
              onClick={login}
              className="group relative w-full py-4 px-6 rounded-xl font-semibold text-white overflow-hidden transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] font-body"
              style={{
                background: 'linear-gradient(135deg, #03A9F4 0%, #0288D1 50%, #0277BD 100%)',
                boxShadow: '0 10px 30px -10px rgba(3,169,244,0.5), 0 0 0 1px rgba(255,255,255,0.1) inset',
              }}
            >
              <span className="relative z-10 flex items-center justify-center gap-3">
                <svg className="w-5 h-5" viewBox="0 0 23 23" fill="currentColor">
                  <path d="M0 0h11v11H0zM12 0h11v11H12zM0 12h11v11H0zM12 12h11v11H12z"/>
                </svg>
                Sign in with Microsoft
              </span>
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  background: 'linear-gradient(135deg, #29B6F6 0%, #03A9F4 100%)',
                }}
              />
            </button>

            {/* MSCAA attribution */}
            <div className="mt-8 pt-6 border-t border-white/5">
              <p className="text-center text-slate-400 text-xs leading-relaxed font-body">
                Developed for the<br/>
                <span className="text-slate-300 font-medium">Memphis-Shelby County Airport Authority</span>
              </p>
            </div>
          </div>

          {/* Security notice */}
          <div
            className="mt-6 text-center"
            style={{ animation: 'fadeInUp 0.8s ease-out 0.3s both' }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20">
              <svg className="w-4 h-4 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span className="text-amber-300/90 text-xs font-medium tracking-wide font-body">
                Unauthorized access is prohibited
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="absolute bottom-6 left-0 right-0 text-center"
          style={{ animation: 'fadeIn 1s ease-out 0.5s both' }}
        >
          <p className="text-slate-500 text-xs font-body">
            MEM &middot; Frederick W Smith International Airport
          </p>
        </div>
      </div>

      {/* Keyframes */}
      <style>{`
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes logoFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        @keyframes particle-float {
          0% { transform: translateY(100vh) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(-100vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

function AccessDeniedScreen() {
  const { logout } = useAuth();

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-primary px-4">
      <div className="w-full max-w-md text-center">
        <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full bg-error/10">
          <svg className="h-8 w-8 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
        </div>
        <h1 className="mb-2 font-heading text-4xl font-bold text-error">
          Access Denied
        </h1>
        <h2 className="mb-4 font-heading text-xl font-semibold text-text-primary">
          No AI Hub Access Group
        </h2>
        <p className="mb-8 font-body text-text-secondary">
          Your account is not a member of any AI Hub access group.
          Contact your administrator to request access.
        </p>
        <button
          onClick={logout}
          className="rounded-lg bg-surface-secondary px-6 py-3 font-body text-sm font-medium text-text-primary transition-colors hover:bg-surface-tertiary"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

export function RequireAuth() {
  const { accessDenied, isLoading } = useAuth();

  return (
    <>
      <AuthenticatedTemplate>
        {isLoading ? (
          <div className="flex min-h-screen items-center justify-center">
            <div className="text-center">
              <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              <p className="font-body text-text-secondary">Verifying access...</p>
            </div>
          </div>
        ) : accessDenied ? (
          <AccessDeniedScreen />
        ) : (
          <Outlet />
        )}
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <LoginScreen />
      </UnauthenticatedTemplate>
    </>
  );
}
